import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock'
import { visualRoute } from '../utils/routePresentation'
/**
 * Arquivo: src/components/MobileBottomNav.tsx
 * Versão: 4.10.98
 * Data: 17/09/2026
 */
import { useEffect, useState } from 'react'
import {
  ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, BarChart3, 
  LogOut, MoreHorizontal, Plus, X, BookOpen, CreditCard, Landmark, TrendingUp, Repeat2, Wallet, Target, ChartNoAxesCombined, Calculator, ListChecks, FolderTree, ShieldCheck, History, Settings,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { protegerAposFechamentoPorToque } from '../utils/touchInteractionGuard'
import { useQuickCreateAction } from '../context/QuickCreateContext'

type Props = { onLogout: () => void | Promise<void> }
type Item = { to: string; label: string; icon: typeof Plus }

const maisGrupos: Array<{ titulo: string; itens: Item[] }> = [
  { titulo: 'Seu dinheiro', itens: [
    { to: '/lancamentos', icon: BookOpen, label: 'Lançamentos' },
    { to: '/transferencias', icon: ArrowLeftRight, label: 'Transferências' },
    { to: '/cartoes', icon: CreditCard, label: 'Cartões' },
    { to: '/contas', icon: Landmark, label: 'Contas e Bancos' },
    { to: '/investimentos', icon: TrendingUp, label: 'Investimentos' },
  ]},
  { titulo: 'Planejamento', itens: [
    { to: '/recorrencias', icon: Repeat2, label: 'Recorrências' },
    { to: '/orcamentos', icon: Wallet, label: 'Orçamentos' },
    { to: '/metas', icon: Target, label: 'Metas' },
  ]},
  { titulo: 'Análises', itens: [
    { to: '/relatorios', icon: ChartNoAxesCombined, label: 'Relatórios' },
    { to: '/fiscal', icon: Calculator, label: 'Apuração Fiscal' },
    { to: '/conciliacao', icon: ListChecks, label: 'Conciliação' },
  ]},
  { titulo: 'Sistema', itens: [
    { to: '/plano-contas', icon: FolderTree, label: 'Plano de Contas' },
    { to: '/administracao', icon: ShieldCheck, label: 'Administração' },
    { to: '/auditoria', icon: History, label: 'Auditoria' },
    { to: '/configuracoes', icon: Settings, label: 'Configurações' },
  ]},
]
const maisItens = maisGrupos.flatMap(grupo => grupo.itens)

export default function MobileBottomNav({ onLogout }: Props) {
  const location = useLocation()
  const presentationPath = visualRoute(location.pathname, location.search, location.state)
  const quickCreate = useQuickCreateAction()
  const navigate = useNavigate()
  const [maisAberto, setMaisAberto] = useState(false)
  const [lancarAberto, setLancarAberto] = useState(false)
  useOverlayScrollLock(maisAberto || lancarAberto)

  useEffect(() => {
    setMaisAberto(false)
    setLancarAberto(false)
  }, [location.pathname, location.search])

  useEffect(() => {
    const aberto = maisAberto || lancarAberto
    const fecharComEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMaisAberto(false)
        setLancarAberto(false)
      }
    }
    if (aberto) window.addEventListener('keydown', fecharComEscape)
    return () => {
      window.removeEventListener('keydown', fecharComEscape)
    }
  }, [maisAberto, lancarAberto])

  const ir = (to: string) => navigate(to)
  const iniciarLancamento = () => {
    // Só o Início oferece a escolha entre saída, entrada e transferência.
    if (location.pathname === '/') { setLancarAberto(true); return }
    // Nenhuma rota cai em um menu financeiro genérico: a tela registra a ação.
    quickCreate?.execute()
  }
  const ativo = (to: string) => to === '/' ? presentationPath === '/' : presentationPath.startsWith(to)
  const maisAtivo = maisItens.some(item => ativo(item.to))
  const fecharPaineis = (detalheDoClique = 1) => {
    protegerAposFechamentoPorToque(detalheDoClique)
    setMaisAberto(false)
    setLancarAberto(false)
  }

  return (
    <>
      {(maisAberto || lancarAberto) && (
        <button className="mobile-sheet-overlay" aria-label="Fechar" onClick={(event) => { event.preventDefault(); event.stopPropagation(); fecharPaineis(event.detail) }} />
      )}

      {lancarAberto && (
        <section className="mobile-action-sheet mobile-launch-sheet" role="dialog" aria-modal="true" aria-label="Novo lançamento">
          <div className="mobile-sheet-handle" />
          <div className="mobile-sheet-header">
            <div><strong>Novo lançamento</strong><span>Escolha o tipo</span></div>
            <button className="mobile-sheet-close" onClick={(event) => { event.preventDefault(); event.stopPropagation(); protegerAposFechamentoPorToque(event.detail); setLancarAberto(false) }} aria-label="Fechar"><X size={20}/></button>
          </div>
          <div className="mobile-launch-grid">
            <button onClick={() => ir('/lancamentos?novo=despesa')}><ArrowDownCircle size={22}/><span>Saída</span></button>
            <button onClick={() => ir('/lancamentos?novo=receita')}><ArrowUpCircle size={22}/><span>Entrada</span></button>
            <button onClick={() => ir('/transferencias?novo=1')}><ArrowLeftRight size={22}/><span>Transferência</span></button>
          </div>
        </section>
      )}

      {maisAberto && (
        <section className="mobile-action-sheet mobile-more-sheet" role="dialog" aria-modal="true" aria-label="Mais opções">
          <div className="mobile-sheet-handle" />
          <div className="mobile-sheet-header">
            <div><strong>Mais</strong><span>Navegue por categoria</span></div>
            <button className="mobile-sheet-close" onClick={(event) => { event.preventDefault(); event.stopPropagation(); protegerAposFechamentoPorToque(event.detail); setMaisAberto(false) }} aria-label="Fechar"><X size={20}/></button>
          </div>
          <div className="mobile-more-groups">
            {maisGrupos.map(grupo => (
              <section className="mobile-more-group" key={grupo.titulo}>
                <strong className="mobile-more-group-title">{grupo.titulo}</strong>
                <div className="mobile-more-grid">
                  {grupo.itens.map(({ to, label, icon: Icon }) => (
                    <button key={to} type="button" className={ativo(to) ? 'is-active' : ''} aria-current={ativo(to) ? 'page' : undefined} onClick={() => ir(to)}>
                      <span className="mobile-more-tile-icon"><Icon size={16} strokeWidth={1.8} aria-hidden="true"/></span>
                      <span className="mobile-more-tile-label">{label}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
          <button className="mobile-logout" onClick={() => void onLogout()}><LogOut size={18}/> Sair</button>
        </section>
      )}

      <nav className="ff-dash-nav" aria-label="Navegação principal">
        <button className={ativo('/') ? 'active' : ''} aria-current={ativo('/') ? 'page' : undefined} onClick={() => ir('/')}><BarChart3 size={21}/><span>Início</span></button>
        <button className={ativo('/pagar') ? 'active' : ''} aria-current={ativo('/pagar') ? 'page' : undefined} onClick={() => ir('/pagar')}><ArrowDownCircle size={21}/><span>Saídas</span></button>
        <button className="mobile-new-button" onClick={iniciarLancamento} disabled={location.pathname !== '/' && !quickCreate} aria-label={location.pathname === '/' ? 'Novo lançamento' : quickCreate?.label || 'Cadastro indisponível nesta tela'} title={location.pathname === '/' ? 'Escolher lançamento' : quickCreate?.label || 'Nenhum cadastro disponível'}><span><Plus size={25}/></span><em>{location.pathname === '/' ? 'Lançar' : 'Novo'}</em></button>
        <button className={ativo('/receber') ? 'active' : ''} aria-current={ativo('/receber') ? 'page' : undefined} onClick={() => ir('/receber')}><ArrowUpCircle size={21}/><span>Entradas</span></button>
        <button className={maisAberto || maisAtivo ? 'active' : ''} aria-expanded={maisAberto} onClick={() => setMaisAberto(true)}><MoreHorizontal size={22}/><span>Mais</span></button>
      </nav>
    </>
  )
}

